import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { 
  Package, Clock, Truck, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Calendar, MapPin, User, Phone, ChevronDown, ChevronUp,
  Search, Filter, Eye, Trash2, X, ShoppingBag, Euro, FileText,
  ChefHat, Navigation, PackageCheck, Ban, History, TrendingUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  "Nuovo": { 
    color: "bg-blue-500", 
    bgLight: "bg-blue-500/10",
    border: "border-blue-500/30",
    text: "text-blue-500", 
    icon: Package,
    nextStatus: "In preparazione",
    nextLabel: "Inizia Preparazione"
  },
  "In preparazione": { 
    color: "bg-yellow-500", 
    bgLight: "bg-yellow-500/10",
    border: "border-yellow-500/30",
    text: "text-yellow-500", 
    icon: ChefHat,
    nextStatus: "In consegna",
    nextLabel: "Spedisci"
  },
  "In consegna": { 
    color: "bg-orange-500", 
    bgLight: "bg-orange-500/10",
    border: "border-orange-500/30",
    text: "text-orange-500", 
    icon: Navigation,
    nextStatus: "Consegnato",
    nextLabel: "Conferma Consegna"
  },
  "Consegnato": { 
    color: "bg-green-500", 
    bgLight: "bg-green-500/10",
    border: "border-green-500/30",
    text: "text-green-500", 
    icon: PackageCheck,
    nextStatus: null,
    nextLabel: null
  },
  "cancelled": { 
    color: "bg-red-500", 
    bgLight: "bg-red-500/10",
    border: "border-red-500/30",
    text: "text-red-500", 
    icon: Ban,
    nextStatus: null,
    nextLabel: null
  }
};

const TIME_SLOTS = ["10:00-12:00", "12:00-14:00", "14:00-16:00", "16:00-18:00", "18:00-20:00"];

const AdminOrdersUnified = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [recoveringTxn, setRecoveringTxn] = useState(null);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, Nuovo, In preparazione, etc.
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("timeline"); // timeline, list, history
  
  // Modals
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const fetchPendingTransactions = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/pending-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingTransactions(response.data || []);
    } catch (error) {
      console.error("Errore caricamento transazioni pendenti:", error);
    }
  }, []);

  const recoverTransaction = async (checkoutReference) => {
    setRecoveringTxn(checkoutReference);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(
        `${API}/admin/recover-transaction/${checkoutReference}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === "recovered") {
        toast.success(`Ordine #${response.data.order_number} recuperato!`);
        fetchOrders();
        fetchPendingTransactions();
      } else if (response.data.status === "already_processed") {
        toast.info(`Ordine già processato: #${response.data.order_number}`);
        fetchPendingTransactions();
      } else {
        toast.error(`Pagamento non completato (stato: ${response.data.status})`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel recupero");
    } finally {
      setRecoveringTxn(null);
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Errore caricamento ordini:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchPendingTransactions();
  }, [fetchOrders]);

  // Auto-refresh ogni 30 secondi
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.patch(`${API}/orders/${orderId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Stato aggiornato: ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const openCancelModal = (orderId) => {
    setCancelOrderId(orderId);
    setCancelReason("");
    setShowCancelModal(true);
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error("Inserisci una motivazione");
      return;
    }
    setCancelling(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/orders/${cancelOrderId}/cancel`, {
        reason: cancelReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ordine cancellato");
      setShowCancelModal(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella cancellazione");
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Sei sicuro di voler ELIMINARE DEFINITIVAMENTE l'ordine "${order.order_number}"?\n\nQuesta azione è irreversibile e l'ordine non sarà più recuperabile.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Ordine ${order.order_number} eliminato`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // Filter orders
  const getFilteredOrders = () => {
    let filtered = [...orders];
    
    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o => 
        o.order_number?.toLowerCase().includes(term) ||
        o.customer_name?.toLowerCase().includes(term) ||
        o.customer_phone?.includes(term) ||
        o.customer_address?.toLowerCase().includes(term) ||
        o.delivery_comune?.toLowerCase().includes(term) ||
        o.customer_email?.toLowerCase().includes(term)
      );
    }
    
    // Status filter
    if (statusFilter === "active") {
      filtered = filtered.filter(o => o.status !== "cancelled" && o.status !== "Consegnato");
    } else if (statusFilter !== "all") {
      filtered = filtered.filter(o => o.status === statusFilter);
    }
    
    // View mode specific filtering
    if (viewMode === "timeline") {
      filtered = filtered.filter(o => {
        const orderDate = o.delivery_date || o.delivery_details?.date;
        return orderDate === selectedDate && o.status !== "cancelled";
      });
    } else if (viewMode === "history") {
      filtered = filtered.filter(o => o.status === "cancelled" || o.status === "Consegnato");
    }
    
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const filteredOrders = getFilteredOrders();

  // Group by time slot for timeline view
  const ordersBySlot = TIME_SLOTS.reduce((acc, slot) => {
    acc[slot] = filteredOrders.filter(o => {
      const orderTime = o.delivery_time || o.delivery_details?.time;
      return orderTime === slot;
    });
    return acc;
  }, {});

  // Statistics
  const todayOrders = orders.filter(o => {
    const orderDate = o.delivery_date || o.delivery_details?.date;
    return orderDate === selectedDate && o.status !== "cancelled";
  });
  
  const stats = {
    total: todayOrders.length,
    nuovo: todayOrders.filter(o => o.status === "Nuovo").length,
    preparazione: todayOrders.filter(o => o.status === "In preparazione").length,
    consegna: todayOrders.filter(o => o.status === "In consegna").length,
    consegnato: todayOrders.filter(o => o.status === "Consegnato").length,
    revenue: todayOrders.reduce((sum, o) => sum + (o.total || 0), 0)
  };

  // Generate next 14 days
  const getNextDays = () => {
    const days = [];
    for (let i = 0; i < 14; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      days.push(date.toISOString().split("T")[0]);
    }
    return days;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT", { 
      weekday: "short", day: "numeric", month: "short" 
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("it-IT", { 
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
    });
  };

  // Order Card Component
  const OrderCard = ({ order, compact = false }) => {
    const config = STATUS_CONFIG[order.status] || STATUS_CONFIG["Nuovo"];
    const StatusIcon = config.icon;
    const isExpanded = expandedOrder === order.id;

    return (
      <div className={`${config.bgLight} ${config.border} border rounded-xl overflow-hidden transition-all`}>
        {/* Header */}
        <div className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`${config.color} w-10 h-10 rounded-lg flex items-center justify-center`}>
                <StatusIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-bold">{order.order_number}</span>
                  <span className={`${config.bgLight} ${config.text} text-xs font-semibold px-2 py-0.5 rounded-full border ${config.border}`}>
                    {order.status === "cancelled" ? "Cancellato" : order.status}
                  </span>
                  {order.delivery_type === "aziendale" && (
                    <span className="bg-blue-500/20 text-blue-400 text-xs font-bold px-2 py-0.5 rounded-full border border-blue-500/30" data-testid={`company-order-badge-${order.id}`}>
                      AZIENDALE {order.partner_company_name ? `- ${order.partner_company_name}` : ""}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm text-zinc-400 mt-1">
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {order.customer_name || "Cliente"}
                  </span>
                  {order.admin_note && (
                    <span className="text-yellow-500 text-xs font-medium">ADMIN: {order.admin_note}</span>
                  )}
                  {order.customer_phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {order.customer_phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-white font-bold text-lg">€{(order.total || 0).toFixed(2)}</p>
              <p className="text-zinc-500 text-xs">{order.items?.length || 0} articoli</p>
              {/* Stato Pagamento */}
              {order.payment_status === 'paid' || order.payment_method === 'sumup' || order.payment_method === 'stripe' ? (
                <span className="inline-block mt-1 px-2 py-0.5 bg-green-500 text-black text-xs font-bold rounded">
                  ✓ PAGATO
                </span>
              ) : (
                <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-500 text-black text-xs font-bold rounded">
                  DA PAGARE
                </span>
              )}
            </div>
          </div>

          {/* Delivery Info */}
          <div className="flex items-center gap-4 mt-3 text-sm">
            <span className="flex items-center gap-1 text-zinc-400">
              <Calendar className="w-4 h-4" />
              {formatDate(order.delivery_date || order.delivery_details?.date)}
            </span>
            <span className="flex items-center gap-1 text-zinc-400">
              <Clock className="w-4 h-4" />
              {order.delivery_time || order.delivery_details?.time || "-"}
            </span>
          </div>

          {/* Indirizzo Consegna - BEN VISIBILE */}
          <div className="mt-3 bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <MapPin className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-500 text-xs font-semibold uppercase tracking-wider">Indirizzo Consegna</p>
                <p className="text-white font-medium">{order.customer_address || order.delivery_address || "-"}</p>
                {order.delivery_comune && (
                  <p className="text-zinc-300 text-sm">Comune: <span className="text-white">{order.delivery_comune}</span> {order.delivery_zone && <span className="text-zinc-400">• Zona {order.delivery_zone}</span>}</p>
                )}
              </div>
            </div>
          </div>

          {/* Cancellation reason */}
          {order.status === "cancelled" && order.cancellation_reason && (
            <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg p-2">
              <p className="text-red-400 text-sm">
                <strong>Motivo:</strong> {order.cancellation_reason}
              </p>
            </div>
          )}

          {/* Actions */}
          {order.status !== "cancelled" && (
            <div className="flex items-center gap-2 mt-4">
              {config.nextStatus && (
                <button
                  onClick={() => handleStatusChange(order.id, config.nextStatus)}
                  className={`${config.color} text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-all flex items-center gap-2`}
                >
                  {config.nextLabel}
                </button>
              )}
              
              <button
                onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                className="bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-zinc-700 transition-all flex items-center gap-1"
              >
                <Eye className="w-4 h-4" />
                Dettagli
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {order.status !== "Consegnato" && (
                <button
                  onClick={() => openCancelModal(order.id)}
                  className="bg-red-500/20 text-red-500 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-500/30 transition-all flex items-center gap-1"
                  title="Annulla ordine"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
              
              <button
                onClick={() => handleDeleteOrder(order)}
                className="bg-zinc-800 text-zinc-400 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-500/20 hover:text-red-500 transition-all flex items-center gap-1"
                title="Elimina definitivamente"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t border-zinc-800 p-4 bg-black/30">
            {/* Delivery Details - Full Address */}
            <div className="mb-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <h4 className="text-yellow-500 font-semibold mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Dettagli Consegna
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-zinc-500">Indirizzo completo</p>
                  <p className="text-white font-medium">{order.customer_address || order.delivery_address || "-"}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Comune</p>
                  <p className="text-white font-medium">{order.delivery_comune || "-"}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Zona</p>
                  <p className="text-white font-medium">{order.delivery_zone || "-"}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Email</p>
                  <p className="text-white font-medium">{order.customer_email || "-"}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Codice Fiscale</p>
                  <p className="text-white font-medium">{order.customer_fiscal_code || "-"}</p>
                </div>
              </div>
              {(order.delivery_notes || order.notes) && (
                <div className="mt-3 pt-3 border-t border-yellow-500/20">
                  <p className="text-zinc-500 text-sm flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    Note consegna:
                  </p>
                  <p className="text-white mt-1">{order.delivery_notes || order.notes}</p>
                </div>
              )}
            </div>

            {/* Products */}
            <div className="mb-4">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4" />
                Prodotti ordinati
              </h4>
              <div className="space-y-2">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="bg-zinc-900 rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="bg-zinc-800 text-white text-xs font-bold px-2 py-1 rounded">
                          x{item.quantity}
                        </span>
                        <span className="text-white">{item.name}</span>
                      </div>
                      <span className="text-green-500 font-semibold">{"\u20AC"}{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                    {item.batches && item.batches.length > 0 && (
                      <div className="mt-1 ml-10 space-y-1">
                        {item.batches.map((b, bidx) => (
                          <div key={`${b.lotto}-${b.scadenza}`} className="text-xs text-zinc-400 flex gap-3">
                            <span>Lotto: <span className="text-yellow-400 font-semibold">{b.lotto}</span></span>
                            <span>Scad: <span className="text-yellow-400 font-semibold">{new Date(b.scadenza).toLocaleDateString("it-IT")}</span></span>
                            {b.quantita !== item.quantity && <span>Qtà: {b.quantita}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-500">Subtotale</p>
                <p className="text-white">€{(order.subtotal || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-zinc-500">Spedizione</p>
                <p className="text-white">€{(order.shipping || 0).toFixed(2)}</p>
              </div>
              {order.discount > 0 && (
                <div>
                  <p className="text-zinc-500">Sconto volume</p>
                  <p className="text-green-500">-€{(order.discount || 0).toFixed(2)}</p>
                </div>
              )}
              {order.coupon_discount > 0 && (
                <div>
                  <p className="text-zinc-500">Codice sconto</p>
                  <p className="text-green-500">-€{(order.coupon_discount || 0).toFixed(2)}</p>
                </div>
              )}
              {order.business_payment_discount > 0 && (
                <div>
                  <p className="text-zinc-500">Sconto anticipato (azienda)</p>
                  <p className="text-green-500">-€{(order.business_payment_discount || 0).toFixed(2)}</p>
                </div>
              )}
              {order.private_payment_discount > 0 && (
                <div>
                  <p className="text-zinc-500">Sconto anticipato (1%)</p>
                  <p className="text-green-500">-€{(order.private_payment_discount || 0).toFixed(2)}</p>
                </div>
              )}
              {order.birthday_discount > 0 && (
                <div>
                  <p className="text-zinc-500">Sconto compleanno</p>
                  <p className="text-green-500">-€{(order.birthday_discount || 0).toFixed(2)}</p>
                </div>
              )}
              {order.partner_discount > 0 && (
                <div>
                  <p className="text-zinc-500">Sconto aziendale{order.partner_company_name ? ` (${order.partner_company_name})` : ""}</p>
                  <p className="text-blue-400">-€{(order.partner_discount || 0).toFixed(2)}</p>
                </div>
              )}
              <div>
                <p className="text-zinc-500">IVA (10%)</p>
                <p className="text-white">€{(order.iva || order.vat || 0).toFixed(2)}</p>
              </div>
            </div>

            {/* Status Change Buttons */}
            {order.status !== "cancelled" && order.status !== "Consegnato" && (
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <p className="text-zinc-500 text-sm mb-2">Cambia stato:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_CONFIG).filter(([key]) => key !== "cancelled").map(([status, cfg]) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(order.id, status)}
                      disabled={order.status === status}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        order.status === status
                          ? `${cfg.color} text-white`
                          : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="mt-4 pt-4 border-t border-zinc-800 text-xs text-zinc-500">
              <p>Creato: {formatDateTime(order.created_at)}</p>
              {order.cancelled_at && <p>Cancellato: {formatDateTime(order.cancelled_at)}</p>}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="orders-unified">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Truck className="w-8 h-8 text-green-500" />
              Gestione Ordini
            </h1>
            {lastUpdate && (
              <p className="text-zinc-500 text-sm mt-1 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Ultimo aggiornamento: {lastUpdate.toLocaleTimeString("it-IT")}
                {autoRefresh && <span className="text-green-500">(live)</span>}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                autoRefresh ? "bg-green-500 text-black" : "bg-zinc-800 text-zinc-400"
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${autoRefresh ? "animate-spin" : ""}`} />
              {autoRefresh ? "Live ON" : "Live OFF"}
            </button>
            <button
              onClick={fetchOrders}
              className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Aggiorna
            </button>
          </div>
        </div>

        {/* Banner Transazioni Pendenti */}
        {pendingTransactions.length > 0 && (
          <div className="bg-orange-500/10 border border-orange-500 rounded-lg p-4" data-testid="pending-transactions-banner">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                <h3 className="text-orange-500 font-bold">
                  {pendingTransactions.length} pagament{pendingTransactions.length === 1 ? 'o' : 'i'} SumUp in sospeso
                </h3>
              </div>
              <button
                onClick={async () => {
                  if (window.confirm("Eliminare TUTTE le transazioni in sospeso?")) {
                    try {
                      const token = localStorage.getItem("admin_token");
                      const res = await axios.delete(`${API}/admin/pending-transactions`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success(res.data.message);
                      fetchPendingTransactions();
                    } catch (e) {
                      toast.error("Errore nell'eliminazione");
                    }
                  }
                }}
                className="text-red-400 hover:text-red-300 text-sm font-semibold flex items-center gap-1"
                data-testid="delete-all-pending"
              >
                <Trash2 className="w-4 h-4" />
                Elimina tutte
              </button>
            </div>
            <p className="text-zinc-400 text-sm mb-3">
              Questi pagamenti potrebbero essere stati completati su SumUp ma l'ordine non è stato creato. Clicca "Recupera" per verificare e creare l'ordine.
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {pendingTransactions.slice(0, 10).map((txn) => (
                <div key={txn.checkout_reference} className="flex items-center justify-between bg-black/30 rounded-lg px-4 py-2">
                  <div className="text-sm">
                    <span className="text-white font-medium">{txn.order_data?.customer_name || "N/A"}</span>
                    <span className="text-zinc-500 mx-2">|</span>
                    <span className="text-green-400 font-semibold">{"\u20AC"}{txn.amount?.toFixed(2)}</span>
                    <span className="text-zinc-500 mx-2">|</span>
                    <span className="text-zinc-400">{new Date(txn.created_at).toLocaleString("it-IT", {day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"})}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => recoverTransaction(txn.checkout_reference)}
                      disabled={recoveringTxn === txn.checkout_reference}
                      className="bg-orange-500 text-black px-3 py-1 rounded-lg text-sm font-semibold hover:bg-orange-400 disabled:opacity-50 flex items-center gap-1"
                    >
                      {recoveringTxn === txn.checkout_reference ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <CheckCircle className="w-3 h-3" />
                      )}
                      Recupera
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm("Eliminare questa transazione?")) {
                          try {
                            const token = localStorage.getItem("admin_token");
                            await axios.delete(`${API}/admin/pending-transactions/${txn.checkout_reference}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success("Transazione eliminata");
                            fetchPendingTransactions();
                          } catch (e) {
                            toast.error("Errore nell'eliminazione");
                          }
                        }
                      }}
                      className="bg-red-500/20 text-red-400 px-3 py-1 rounded-lg text-sm font-semibold hover:bg-red-500/30 flex items-center gap-1"
                      data-testid={`delete-txn-${txn.checkout_reference}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View Mode Tabs */}
        <div className="flex gap-2 border-b border-zinc-800 pb-4">
          <button
            onClick={() => setViewMode("timeline")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              viewMode === "timeline" ? "bg-green-500 text-black" : "bg-zinc-900 text-white hover:bg-zinc-800"
            }`}
          >
            <Calendar className="w-5 h-5" />
            Timeline Consegne
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              viewMode === "list" ? "bg-green-500 text-black" : "bg-zinc-900 text-white hover:bg-zinc-800"
            }`}
          >
            <Package className="w-5 h-5" />
            Tutti gli Ordini
          </button>
          <button
            onClick={() => setViewMode("history")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              viewMode === "history" ? "bg-green-500 text-black" : "bg-zinc-900 text-white hover:bg-zinc-800"
            }`}
          >
            <History className="w-5 h-5" />
            Storico
          </button>
        </div>

        {/* Stats (only in timeline mode) */}
        {viewMode === "timeline" && (
          <>
            {/* Date Selector */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {getNextDays().map((date) => {
                const dayOrders = orders.filter(o => {
                  const orderDate = o.delivery_date || o.delivery_details?.date;
                  return orderDate === date && o.status !== "cancelled";
                });
                const isToday = date === new Date().toISOString().split("T")[0];
                
                return (
                  <button
                    key={date}
                    onClick={() => setSelectedDate(date)}
                    className={`px-4 py-3 rounded-xl font-semibold whitespace-nowrap transition-all flex flex-col items-center min-w-[90px] ${
                      selectedDate === date
                        ? "bg-green-500 text-black"
                        : "bg-zinc-900 text-white hover:bg-zinc-800 border border-zinc-800"
                    }`}
                  >
                    <span className="text-xs opacity-70">
                      {isToday ? "OGGI" : new Date(date).toLocaleDateString("it-IT", { weekday: "short" }).toUpperCase()}
                    </span>
                    <span className="text-xl font-bold">{new Date(date).getDate()}</span>
                    {dayOrders.length > 0 && (
                      <span className={`text-xs mt-1 px-2 py-0.5 rounded-full font-bold ${
                        selectedDate === date ? "bg-black/20" : "bg-green-500/20 text-green-500"
                      }`}>
                        {dayOrders.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-white" />
                  <div>
                    <p className="text-zinc-500 text-xs">Totale</p>
                    <p className="text-2xl font-bold text-white">{stats.total}</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-zinc-500 text-xs">Nuovi</p>
                    <p className="text-2xl font-bold text-blue-500">{stats.nuovo}</p>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <ChefHat className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-zinc-500 text-xs">Preparazione</p>
                    <p className="text-2xl font-bold text-yellow-500">{stats.preparazione}</p>
                  </div>
                </div>
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Navigation className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-zinc-500 text-xs">In Consegna</p>
                    <p className="text-2xl font-bold text-orange-500">{stats.consegna}</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <PackageCheck className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-zinc-500 text-xs">Consegnati</p>
                    <p className="text-2xl font-bold text-green-500">{stats.consegnato}</p>
                  </div>
                </div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Euro className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-zinc-500 text-xs">Incasso</p>
                    <p className="text-xl font-bold text-green-500">€{stats.revenue.toFixed(0)}</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Search & Filters (for list and history modes) */}
        {(viewMode === "list" || viewMode === "history") && (
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca per numero ordine, cliente, telefono..."
                className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-white focus:outline-none focus:border-green-500"
              />
            </div>
            {viewMode === "list" && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500"
              >
                <option value="all">Tutti gli stati</option>
                <option value="active">Solo attivi</option>
                <option value="Nuovo">Nuovo</option>
                <option value="In preparazione">In preparazione</option>
                <option value="In consegna">In consegna</option>
                <option value="Consegnato">Consegnato</option>
                <option value="cancelled">Cancellato</option>
              </select>
            )}
          </div>
        )}

        {/* Content */}
        {viewMode === "timeline" ? (
          /* Timeline View - Orders grouped by time slot */
          <div className="space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
                <Truck className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400 text-lg">Nessuna consegna per {formatDate(selectedDate)}</p>
              </div>
            ) : (
              TIME_SLOTS.map((slot) => {
                const slotOrders = ordersBySlot[slot];
                if (slotOrders.length === 0) return null;

                return (
                  <div key={slot} className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                    <div className="bg-zinc-800 px-4 py-3 flex items-center gap-3">
                      <Clock className="w-5 h-5 text-green-500" />
                      <span className="text-white font-bold text-lg">{slot}</span>
                      <span className="bg-green-500 text-black text-sm font-bold px-2.5 py-0.5 rounded-full">
                        {slotOrders.length}
                      </span>
                    </div>
                    <div className="p-4 space-y-4">
                      {slotOrders.map((order) => (
                        <OrderCard key={order.id} order={order} />
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          /* List / History View */
          <div className="space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
                <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400 text-lg">
                  {viewMode === "history" ? "Nessun ordine nello storico" : "Nessun ordine trovato"}
                </p>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))
            )}
          </div>
        )}

        {/* Legend */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-400 text-sm mb-3">Legenda stati:</p>
          <div className="flex flex-wrap gap-4">
            {Object.entries(STATUS_CONFIG).map(([status, config]) => {
              const Icon = config.icon;
              return (
                <div key={status} className="flex items-center gap-2">
                  <span className={`${config.color} w-3 h-3 rounded-full`}></span>
                  <Icon className={`w-4 h-4 ${config.text}`} />
                  <span className="text-zinc-300 text-sm">{status === "cancelled" ? "Cancellato" : status}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Trash2 className="w-6 h-6 text-red-500" />
                Cancella Ordine
              </h2>
              <button onClick={() => setShowCancelModal(false)} className="text-zinc-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
              <p className="text-red-400 text-sm">
                Questa azione non può essere annullata.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white font-semibold mb-2">Motivazione *</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Es: Richiesta dal cliente, Prodotto non disponibile..."
                  rows={3}
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 bg-zinc-800 text-white py-3 rounded-lg font-semibold hover:bg-zinc-700"
                >
                  Annulla
                </button>
                <button
                  onClick={handleCancelOrder}
                  disabled={cancelling || !cancelReason.trim()}
                  className="flex-1 bg-red-500 text-white py-3 rounded-lg font-bold hover:bg-red-600 disabled:bg-zinc-700 disabled:text-zinc-500"
                >
                  {cancelling ? "Cancellando..." : "Conferma"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrdersUnified;
